import React, { useState, useRef, useEffect } from "react";
import { PrimaryButton } from "Components/Button";
import { Input, Select } from "Components/Form";
import BooleanSelect from "Components/Form/BooleanSelect";
import MeApi from 'Services/Api/Me/Me';
import MailLink from "Components/Utilities/MailLink";
import CurrentUser from 'Services/User';
import {getFallbackAvatarUrl} from 'Services/AuthHelpers';
import { FileUpload } from "Components/Form";
import CountrySelect from "Components/Form/CountrySelect";

const marketUnits = ['APAC', 'BGIUK', 'ELA', 'Saudi Arabia', 'Group'];
const regions = ['Europe', 'North America', 'South America', 'Africa', 'Asia', 'Australasia'];
const businessUnits = {
    'APAC': ['Bupa Health Insurance Australia', 'Bupa Health Services Australia', 'Bupa Villages & Aged Care Australia', 'Bupa Villages & Aged Care New Zealand', 'Bupa Hong Kong',' Quality HealthCare Hong Kong'],
    'BGIUK': ['UK Insurance', 'Bupa Dental Care', 'Health Services', 'Care Services', 'Bupa Global', 'Niva Bupa'],
    'ELA': ['Sanitas Seguros', 'Sanitas Hospitales', 'Sanitas Dental', 'Sanitas Mayores', 'Bupa Chile', 'LUX MED', 'Bupa Turkiye', 'Bupa Global Latin America', 'Bupa Mexico', 'Care Plus'],
    //'KSA': ['Bupa Arabia', 'My Clinic'],
    'Saudi Arabia': ['Bupa Arabia', 'My Clinic'],
    'Group': ['Group functions']
};

// Places held back for executives. Kept out of the general allocation, so registering one
// never touches capacity or the waitlist — see App\Models\User::GROUP_EXECUTIVES.
const EXECUTIVE_GROUP = 'executives';

const organizationTypes = [
    'Public healthcare',
    'Private healthcare',
    'Pharmaceuticals',
    'Financial services',
    'Technology services',
    'Consulting services',
    'Government',
    'Media services',
    'Regulator',
    'Not-for-profit organisation and/or charity',
    'Student',
    'Other'
];

const RegistrationForm = ({ form, onSubmit, onSuccess, isPastDeadline, isAdmin=false,  ...props }) => {
    const { working, handleSubmit, getFieldError, handleInput } = props;
    const [step, setStep] = useState(1);
    // Create a stable id to use for region inputs to prevent browsers recognising them as address/region fields
    const regionIdRef = useRef(Math.random().toString(36).slice(2, 9));
    const regionInputName = (suffix = '') => `region_${regionIdRef.current}${suffix ? '_' + suffix : ''}`;

    // determine admin status from props or current user

    // Deadline for changing group to virtual
    // After this date, all registrations will be virtual only

    // const goToStep2 = () => {
    //     //if the email address contains bupa.com, set the group to internal
    //     if (form.email.includes('bupa.com')) {
    //         if (currentDate > deadline) {
    //             form.group =  'Internal Virtual';
    //         } else {
    //             console.log('setting group to internal');
    //             form.group =  'Internal';
    //         }
    //     } else {
    //         if (currentDate > deadline) {
    //             form.group =  'External Virtual';
    //         } else {
    //             form.group =  'External';
    //         }
    //     }
    //     setStep(2);
    // };

    const [errorMessage, setErrorMessage] = useState(null);
    const [errorMessageInteral, setErrorMessageInternal] = useState(null);
    const [attendingDisabled, setAttendingDisabled] = useState(false);
    const [notAttendingDisabled, setNotAttendingDisabled] = useState(false);
    const [inPersonFull, setInPersonFull] = useState(false);
    // The delegate type the email lookup came back with, kept so the executive route can
    // still show the right form and so switching back off it restores the normal group.
    const [lookupType, setLookupType] = useState(null);

    // The executive route is only ever offered to an admin: either the admin area's embedded
    // form, or an admin signed in on the public page registering someone at the desk.
    const adminUser = isAdmin === true
        || isAdmin === 'true'
        || (CurrentUser.loggedIn && CurrentUser.data?.is_admin === true);

    // Check capacity up front so the first step can warn about the waitlist before we
    // know who is registering. The email lookup refreshes this on the way to step 2.
    useEffect(() => {
        let cancelled = false;

        (async () => {
            const response = await MeApi.registrationCapacity();

            if (!cancelled && response?.status === 200) {
                setInPersonFull(response.data?.in_person_full === true);
            }
        })();

        return () => { cancelled = true; };
    }, []);

    const lookup = async (form) => {
        setErrorMessage(null);
        setErrorMessageInternal(null);
        setAttendingDisabled(false);
        setNotAttendingDisabled(false);
        const response = await MeApi.registrationLookup(form);
        if (response.status === 300) {
            if (response.error){
                setErrorMessage ( true );
            }

            if (response.error_internal) {
                setErrorMessageInternal ( true );
            }
        }

        if (response.attending_disabled) {
            setAttendingDisabled(true);
        }
        if (response.not_attending_disabled) {
            setNotAttendingDisabled(true);
        }
        setInPersonFull(response.in_person_full === true);

        return response;
    };
    const onType = ({ type }) => {
        handleInput('group', type);
    };

    const handleFormSubmit = async (e) => {
        if (step === 1) {
            const response = await handleSubmit(e, lookup, (res) => {
                handleInput('group', res.type);
                setLookupType(res.type);
                setStep(2);
            });

            // Check if the errorMessage state has been set
            if (errorMessage || errorMessageInteral) {
                setStep(1);
                return;
            }

            return response;
        }

        if (step === 2) {
            // No direct submission here, handled by button clicks
            return;
        }

        return handleSubmit(e, onSubmit, (res) => onSuccess(
            res?.data?.attendance_status,
            form.is_attending === true,
            res?.data?.group || form.group,
            form.afternoon_session_acknowledgement
        ));
    };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const isVirtual = form.group?.indexOf('virtual') !== -1;
    const isExecutive = form.group === EXECUTIVE_GROUP;
    // An executive place can go to a Bupa exec or an external guest, so the fields shown are
    // still the ones the email lookup called for.
    const formGroup = isExecutive
        ? ((lookupType || '').indexOf('internal') !== -1 ? 'internal' : 'external')
        : form.group;
    // A held place is never on the waitlist, so the waitlist copy must not appear for one.
    const showInPersonWaitlistMessage = inPersonFull && !isVirtual && !isExecutive && form.is_attending === true;

    return (
        <>
            {/*{ form.is_attending !== null && form.group && (*/}
            {/*    <div className="bg-app-darker-blue py-10 lg:py-20">*/}
            {/*        <Container>*/}
            {/*            <div className="space-y-6 text-xl text-app-blue text-center">*/}
            {/*                <div className="text-3xl text-app-blue font-bold">*/}
            {/*                    We’re really pleased you are able to join us { isVirtual ? <span className={"font-bold"}> virtually </span> : "from 1:30pm" } on Wednesday 15 October 2025.*/}
            {/*                </div>*/}

            {/*                <p>*/}
            {/*                    Please complete your details below so we can register your { isVirtual ? <span className={"font-bold"}> virtual  </span> : "" } attendance.*/}
            {/*                </p>*/}

            {/*                {!isVirtual && (*/}
            {/*                    <p>*/}
            {/*                        Please note registration will close on 2nd October.*/}
            {/*                    </p>*/}
            {/*                )}*/}
            {/*            </div>*/}
            {/*        </Container>*/}
            {/*    </div>*/}
            {/*) }*/}

            {step === 1 ? (
                <>
                    <p className={ "mt-20 max-w-7xl text-center mx-auto text-app-blue font-bold text-lg md:text-2xl" }>
                        Thank you for your interest in Bupa Symposium 2026.
                    </p>

                    <p className={ "my-10 max-w-7xl text-center mx-auto text-app-blue" }>
                        Please enter your email address below and let us know how you would like to attend.
                    </p>
                    { inPersonFull && (
                        <p className={ "my-4 max-w-5xl text-center mx-auto text-red-600 font-semibold" }>
                            In-person capacity is full, you can still register your details, and we will add you to the waitlist. We will notify you if a space becomes available.
                        </p>
                    ) }
                </>
            ) : step === 2 ? (
                <>
                    <p className={ "mt-20 max-w-7xl text-center mx-auto text-app-blue font-bold text-lg md:text-2xl" }>
                        Thank you for your interest in Bupa Symposium 2026.
                    </p>
                    { inPersonFull && (
                        <p className={ "my-10 max-w-5xl text-center mx-auto text-app-blue" }>
                            In-person capacity is full. You can join the <span className={"font-bold"}>in-person waitlist</span> and we will be in touch once a space becomes available, or you can attend virtually.
                        </p>
                    ) }
                </>
            ) : step === 2 ? (
                <>
                    <p className={ "mt-20 max-w-7xl text-center mx-auto text-app-blue font-bold text-lg md:text-2xl" }>
                        Thank you for your interest in Bupa Symposium 2026.
                    </p>
                </>
            ) : (
                <>
                    <p className={ "mt-20 max-w-7xl text-center mx-auto text-app-blue font-bold text-lg md:text-2xl" }>
                        Thank you for your interest in Bupa Symposium 2026.
                    </p>
                    {showInPersonWaitlistMessage ? (
                        <p className={ "my-10 max-w-5xl text-center mx-auto text-app-blue" }>
                            In-person capacity is full. Please enter your details below to <span className={"font-bold"}>join the waitlist</span>. We will be in touch once a space becomes available.
                        </p>
                    ) : (
                        <p className={ "my-10 max-w-5xl text-center mx-auto text-app-blue" }>
                            You will be joining us {isVirtual ? <span className={"font-bold"}>virtually</span> : <span className={"font-bold"}>in person</span>} on 7 October 2026. Please complete the registration form below to secure your place.
                        </p>
                    )}
                </>
            )}

            <div className="py-10">
                <form
                    autoComplete="off"
                    onSubmit={ handleFormSubmit }
                    className="space-y-6 max-w-2xl mx-auto px-4"
                >
                    {/*{!isAdmin &&
                        <p className={ "my-10 max-w-7xl text-center mx-auto text-app-blue" }>
                            We have reached capacity for our event in London. You can still register to attend the event virtually using the link below
                        </p>
                    }*/}

                    { step === 1 ? (
                        <>
                            <Input
                                onChange={ (v) => handleInput(`email`, v) }
                                type="email"
                                value={ form?.email }
                                placeholder="Email Address*"
                                error={ getFieldError(`email`) }
                                required
                                labelColour={ 'text-app-blue' }
                                label=""
                            />
                            <div className={ "w-full mx-auto flex justify-center gap-5" }>
                                <PrimaryButton
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        const response = await handleSubmit(e, lookup, (res) => {
                                            handleInput('group', res.type);
                                            setLookupType(res.type);
                                            setStep(2);
                                        });
                                        if (errorMessage || errorMessageInteral) {
                                            setStep(1);
                                        }
                                    } }
                                    text={ "Begin Registration" }
                                    disabled={ !emailRegex.test(form?.email) }
                                    working={working}
                                />
                            </div>
                        </>
                    ) : step === 2 ? (
                        <>
                            <div className={ "w-full mx-auto flex justify-center gap-5" }>
                                <PrimaryButton
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        await handleInput('is_attending', true);
                                        // Update group if it was virtualized by previous selection.
                                        // Coming back off the executive route drops them back onto
                                        // the group the email lookup gave us.
                                        let inPersonGroup = form.group === EXECUTIVE_GROUP
                                            ? (lookupType ?? form.group)
                                            : form.group;
                                        if (inPersonGroup === 'internal-virtual') inPersonGroup = 'internal';
                                        if (inPersonGroup === 'external-virtual') inPersonGroup = 'external';
                                        await handleInput('group', inPersonGroup);
                                        setStep(3);
                                    } }
                                    text={ inPersonFull ? "Join the in-person waitlist" : "I can attend in person" }
                                    disabled={ attendingDisabled || !emailRegex.test(form?.email) }
                                    working={working}
                                />
                                <PrimaryButton
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        await handleInput('is_attending', false);
                                        // Update group if attending virtually
                                        let virtualGroup = form.group === EXECUTIVE_GROUP
                                            ? (lookupType ?? form.group)
                                            : form.group;
                                        if (virtualGroup === 'internal') virtualGroup = 'internal-virtual';
                                        if (virtualGroup === 'external') virtualGroup = 'external-virtual';
                                        await handleInput('group', virtualGroup);
                                        setStep(3);
                                    }}
                                    text={ "I can attend virtually" }
                                    disabled={ notAttendingDisabled || !emailRegex.test(form?.email) }
                                    working={working}
                                />
                            </div>

                            { adminUser && (
                                <div className={ "w-full mx-auto max-w-md mt-10 border border-dashed border-app-blue/40 rounded p-4 text-center space-y-2" }>
                                    <p className={ "text-xs uppercase tracking-wide text-app-blue/70 font-bold" }>
                                        Admin only
                                    </p>
                                    <button
                                        type="button"
                                        onClick={async (e) => {
                                            e.preventDefault();
                                            await handleInput('is_attending', true);
                                            await handleInput('group', EXECUTIVE_GROUP);
                                            setStep(3);
                                        }}
                                        disabled={ working || !emailRegex.test(form?.email) }
                                        className={ "text-sm text-app-blue underline disabled:opacity-40 disabled:no-underline" }
                                    >
                                        Register in person against a held executive place
                                    </button>
                                    <p className={ "text-xs text-app-blue/70" }>
                                        Bypasses the waitlist and is not counted against in-person capacity.
                                    </p>
                                </div>
                            ) }
                        </>
                    ) : (
                        <>
                            { ( !errorMessage && !errorMessageInteral ) && (
                                <>
                                    { isExecutive && (
                                        <div className={ "border border-dashed border-app-blue/40 rounded p-4 text-center text-app-blue space-y-1" }>
                                            <p className={ "font-bold" }>Executive held place</p>
                                            <p className={ "text-sm" }>
                                                This registration confirms an in-person place from the executive
                                                allocation. It skips the waitlist and is reported separately from
                                                the general in-person counts.
                                            </p>
                                        </div>
                                    ) }
                                    {
                                        // Use a switch statement to load the correct form based on the group
                                        ( () => {
                                            switch ( formGroup ) {
                                                case  'internal':
                                                case  'speakers':
                                                    return <InternalForm form={ form } { ...props } regionInputName={regionInputName} />;
                                                case 'internal-virtual':
                                                    return <InternalVirtualForm form={ form } { ...props } regionInputName={regionInputName} />;
                                                case 'external':
                                                    return <ExternalForm form={ form } { ...props } regionInputName={regionInputName} />;
                                                case 'external-virtual':
                                                    return <ExternalVirtualForm form={ form } { ...props } regionInputName={regionInputName} />;
                                                default:
                                                    return <CommonFields form={ form } { ...props } />;
                                            }
                                        } ) ()
                                    }
                                    <div className="w-full flex justify-start gap-5">
                                        <PrimaryButton
                                            text="Back"
                                            working={ working }
                                            onClick={ (e) => {
                                                e.preventDefault();
                                                setStep(2);
                                            } }
                                        />
                                        <PrimaryButton
                                            text="Save"
                                            working={ working }
                                        />
                                    </div>
                                </>
                            ) }

                            { errorMessage && (
                                <div className={ "w-full mx-auto flex justify-center gap-5 space-y-10 flex-col" }>
                                    <p className="text-app-blue text-center font-bold">
                                        Thank you for your interest in Bupa Symposium 2026.
                                    </p>
                                    <p className="text-app-blue text-center">
                                        Unfortunately, we've reached our in-person capacity, but we'd love to have you
                                        join us virtually. Please register virtually below and watch the event online.
                                    </p>
                                    <p className="text-app-blue text-center">
                                        If you have any questions, please contact <MailLink
                                        to={ "healthcaresymposium@bupa.com" }
                                        className={ "underline" }
                                    />
                                    </p>
                                    <div className={"max-w-40 mx-auto"}>
                                        <PrimaryButton
                                            onClick={async (e) => {
                                                handleInput('is_attending', false);
                                                const response = await handleSubmit(e, lookup, (res) => {
                                                    handleInput('group', res.type);
                                                    setStep(3);
                                                });
                                            }}
                                            text={ "I can attend virtually" }
                                            disabled={ !emailRegex.test(form?.email) }
                                            working={working}
                                        />
                                    </div>
                                </div>
                            )}

                            { errorMessageInteral && (
                                <div className={ "w-full mx-auto flex justify-center gap-5 space-y-10 flex-col" }>
                                    <p className="text-app-blue text-center font-bold">
                                        Thank you for your interest in Bupa Symposium 2026.
                                    </p>
                                    <p className="text-app-blue text-center">
                                        We currently have limited spaces for internal
                                        guests however we'd love for you to join us virtually on Wednesday
                                        15th October 2025.
                                        Please complete your details below so we can register your virtual attendance.

                                        {!isVirtual && (
                                            <>
                                                Please note registration will close on 2nd October.
                                            </>
                                        )}
                                    </p>
                                    <p className="text-app-blue text-center">
                                        If you have any questions, please contact <MailLink
                                        to={ "healthcaresymposium@bupa.com" }
                                        className={ "underline" }
                                    />
                                    </p>
                                    <div className={"max-w-40 mx-auto"}>
                                        <PrimaryButton
                                            onClick={async (e) => {
                                                handleInput('is_attending', false);
                                                const response = await handleSubmit(e, lookup, (res) => {
                                                    handleInput('group', res.type);
                                                    setStep(3);
                                                });
                                            }}
                                            text={ "I can attend virtually" }
                                            disabled={ !emailRegex.test(form?.email) }
                                            working={working}
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    ) }
                </form>
            </div>
        </>
    );
};

const CommonFields = ({ form, getFieldError, handleInput }) => {
    return (
        <>
            <Input
                onChange={ (v) => handleInput(`first_name`, v) }
                value={ form?.first_name }
                placeholder="First Name*"
                error={ getFieldError(`first_name`) }
                required
                labelColour={ 'text-app-blue' }
                label=""
            />
            <Input
                onChange={ (v) => handleInput(`last_name`, v) }
                value={ form?.last_name }
                placeholder="Last Name*"
                error={ getFieldError(`last_name`) }
                required
                labelColour={ 'text-app-blue' }
                label=""
            />
            <Input
                onChange={ (v) => handleInput(`email`, v) }
                type="email"
                value={ form?.email }
                placeholder={`${form.group?.indexOf('internal') !== -1 ? 'Bupa ' : ''}Email Address*`}
                error={ getFieldError(`email`) }
                required
                labelColour={ 'text-app-blue' }
                label=""
            />
            <Input
                onChange={ (v) => handleInput(`job_title`, v) }
                value={ form?.job_title }
                placeholder="Job Title*"
                error={ getFieldError(`job_title`) }
                required
                labelColour={ 'text-app-blue' }
                label=""
            />
            <Input
                onChange={ (v) => handleInput(`linkedin`, v) }
                value={ form?.linkedin }
                placeholder="Please enter your LinkedIn handle (this will help with your networking profile)"
                error={ getFieldError(`linkedin`) }
                labelColour={ 'text-app-blue' }
                label=""
            />
            <CountrySelect
                value={ form?.country ?? null }
                onChange={ ( v ) => handleInput ( `country`, v ) }
                classes={ `w-full p-2 border border-app-blue rounded` }
                placeholder="Country"
                error={ getFieldError ( `country` ) }
            />
            <div className={"my-10 space-x-6"}>
                {getFieldError ( `avatar_path` ) && (
                    <p className="text-red-500 text-sm">{getFieldError ( `avatar_path` )}</p>
                )}

                <FileUpload
                    containerClassName="m-8 mt-4 flex flex-col justify-center items-center gap-4"
                    value={ form.avatar_path }
                    onChange={ v => handleInput ( 'avatar_path', v ) }
                    accept="image/*"
                    placeholder="Upload profile image (helps with networking)"
                    fallbackImage={ getFallbackAvatarUrl ( form ) }
                    error={ getFieldError ( `avatar_path` ) }
                />
            </div>

            <Input
                labelColour={ "text-app-blue" }
                label=""
                placeholder="Preferred language"
                value={ form?.preferred_language }
                id="preferred_language"
                error={ getFieldError ( 'preferred_language' ) }
                onChange={ ( v ) => handleInput ( 'preferred_language', v ) }
                containerClassName={ "col-span-2 md:col-span-1" }
            />
        </>
    );
};

const InternalForm = ({ form, getFieldError, handleInput, regionInputName }) => {
    let bu = [];

    if (form && form.market_unit && businessUnits[form.market_unit]) {
        bu = businessUnits[form.market_unit];
    }

    return (
        <>
            <Input
                onChange={(v) => handleInput(`first_name`, v)}
                value={form?.first_name}
                placeholder="First Name*"
                error={getFieldError(`first_name`)}
                required
                labelColour={'text-app-blue'}
                label=""
            />

            <Input
                onChange={(v) => handleInput(`last_name`, v)}
                value={form?.last_name}
                placeholder="Last Name*"
                error={getFieldError(`last_name`)}
                required
                labelColour={'text-app-blue'}
                label=""
            />

            <Input
                onChange={(v) => handleInput(`email`, v)}
                type="email"
                value={form?.email}
                placeholder="Bupa Email Address*"
                error={getFieldError(`email`)}
                required
                labelColour={'text-app-blue'}
                label=""
                disabled={true}
            />

            <Input
                onChange={(v) => handleInput(`job_title`, v)}
                value={form?.job_title}
                placeholder="Job Title*"
                error={getFieldError(`job_title`)}
                required
                labelColour={'text-app-blue'}
                label=""
            />

            <Select
                placeholder="Market unit"
                options={marketUnits.map((unit) => ({ value: unit, label: unit }))}
                value={form?.market_unit ?? null}
                onChange={(v) => handleInput(`market_unit`, v)}
                error={getFieldError(`market_unit`)}
                className={'border-app-blue'}
                label=""
            />

            {bu.length > 0 && (
                <Select
                    placeholder="Business unit"
                    options={bu.map((unit) => ({ value: unit, label: unit }))}
                    value={form?.business_unit ?? null}
                    onChange={(v) => handleInput(`business_unit`, v)}
                    error={getFieldError(`business_unit`)}
                    label=""
                />
            )}

            <Select
                placeholder="Region"
                options={regions.map((unit) => ({ value: unit, label: unit }))}
                value={form?.region ?? null}
                onChange={(v) => handleInput(`region`, v)}
                error={getFieldError(`region`)}
                label=""
                inputId={regionInputName ? regionInputName() : undefined}
                name={regionInputName ? regionInputName() : undefined}
                autoComplete="off"
            />

            <Input
                onChange={(v) => handleInput(`dietary_requirements`, v)}
                value={form?.dietary_requirements ?? ''}
                placeholder="Please detail any dietary requirements"
                error={getFieldError(`dietary_requirements`)}
                labelColour={'text-app-blue'}
                label=""
            />

            <Input
                onChange={(v) => handleInput(`special_access_requirements`, v)}
                value={form?.special_access_requirements ?? ''}
                placeholder="Please detail any accessibility requirements"
                error={getFieldError(`special_access_requirements`)}
                labelColour={'text-app-blue'}
                label=""
            />

            <p className={"text-app-blue font-bold"}>
                The Bupa Symposium virtual platform offers the opportunity for you to connect with other attendees. Please choose "No" below if you want your contact information omitted from the platform.
            </p>

            <BooleanSelect
                placeholder="Consent to networking"
                value={form?.consent_networking ?? null}
                onChange={(v) => handleInput(`consent_networking`, v)}
                error={getFieldError(`consent_networking`)}
                label=""
            />

            <p className={"text-app-blue font-bold"}>
                A quick note to let you know that by registering to attend this event in person, you give consent to appear in event photography/videography. If you do not wish to be filmed, please let us know on the event day during checkin.
            </p>

            <Input
                onChange={(v) => handleInput(`password`, v)}
                value={form?.password ?? null}
                placeholder="Password"
                error={getFieldError(`password`)}
                type="password"
                labelColour={'text-app-blue'}
                label=""
            />

            <Input
                onChange={(v) => handleInput(`password_confirmation`, v)}
                value={form?.password_confirmation ?? null}
                placeholder="Reconfirm Password"
                error={getFieldError(`password_confirmation`)}
                type="password"
                labelColour={'text-app-blue'}
                label=""
            />

        </>
    );
};

const InternalVirtualForm = ({ form, getFieldError, handleInput, regionInputName }) => {
    // Determine business units for the selected market unit
    let bu = [];
    if (form && form.market_unit && businessUnits[form.market_unit]) {
        bu = businessUnits[form.market_unit];
    }

    return (
        <>
            <Input
                onChange={(v) => handleInput(`first_name`, v)}
                value={form?.first_name}
                placeholder="First Name*"
                error={getFieldError(`first_name`)}
                required
                labelColour={'text-app-blue'}
                label=""
            />

            <Input
                onChange={(v) => handleInput(`last_name`, v)}
                value={form?.last_name}
                placeholder="Last Name*"
                error={getFieldError(`last_name`)}
                required
                labelColour={'text-app-blue'}
                label=""
            />

            <Input
                onChange={(v) => handleInput(`email`, v)}
                type="email"
                value={form?.email}
                placeholder="Bupa Email Address*"
                error={getFieldError(`email`)}
                required
                labelColour={'text-app-blue'}
                label=""
                disabled={true}
            />

            <Input
                onChange={(v) => handleInput(`job_title`, v)}
                value={form?.job_title}
                placeholder="Job Title*"
                error={getFieldError(`job_title`)}
                required
                labelColour={'text-app-blue'}
                label=""
            />

            <Select
                placeholder="Market unit"
                options={marketUnits.map((unit) => ({ value: unit, label: unit }))}
                value={form?.market_unit ?? null}
                onChange={(v) => handleInput(`market_unit`, v)}
                error={getFieldError(`market_unit`)}
                className={'border-app-blue'}
                label=""
            />

            {bu.length > 0 && (
                <Select
                    placeholder="Business unit"
                    options={bu.map((unit) => ({ value: unit, label: unit }))}
                    value={form?.business_unit ?? null}
                    onChange={(v) => handleInput(`business_unit`, v)}
                    error={getFieldError(`business_unit`)}
                    label=""
                />
            )}

            <Select
                placeholder="Region"
                options={regions.map((region) => ({ value: region, label: region }))}
                value={form?.region ?? null}
                onChange={(v) => handleInput(`region`, v)}
                error={getFieldError(`region`)}
                label=""
                inputId={regionInputName ? regionInputName() : undefined}
                name={regionInputName ? regionInputName() : undefined}
                autoComplete="off"
            />
            <p className={"text-app-blue font-bold"}>
                The Bupa Symposium virtual platform offers the opportunity for you to connect with other attendees. Please choose "No" below if you want your contact information omitted from the platform.
            </p>

            <BooleanSelect
                placeholder="Consent to networking"
                value={form?.consent_networking ?? null}
                onChange={(v) => handleInput(`consent_networking`, v)}
                error={getFieldError(`consent_networking`)}
                label=""
            />

            <Input
                onChange={(v) => handleInput(`password`, v)}
                value={form?.password ?? ''}
                placeholder="Password"
                error={getFieldError(`password`)}
                type="password"
                labelColour={'text-app-blue'}
                label=""
            />

            <Input
                onChange={(v) => handleInput(`password_confirmation`, v)}
                value={form?.password_confirmation ?? ''}
                placeholder="Reconfirm Password"
                error={getFieldError(`password_confirmation`)}
                type="password"
                labelColour={'text-app-blue'}
                label=""
            />
        </>
    );
};

const ExternalForm = ({ form, getFieldError, handleInput, regionInputName }) => {

    return (
        <>
            <Input
                onChange={(v) => handleInput(`first_name`, v)}
                value={form?.first_name}
                placeholder="First Name*"
                error={getFieldError(`first_name`)}
                required
                labelColour={'text-app-blue'}
                label=""
            />
            <Input
                onChange={(v) => handleInput(`last_name`, v)}
                value={form?.last_name}
                placeholder="Last Name*"
                error={getFieldError(`last_name`)}
                required
                labelColour={'text-app-blue'}
                label=""
            />
            <Input
                onChange={(v) => handleInput(`email`, v)}
                type="email"
                value={form?.email}
                placeholder="Email Address*"
                error={getFieldError(`email`)}
                required
                labelColour={'text-app-blue'}
                label=""
                disabled={true}
            />
            <Input
                onChange={(v) => handleInput(`job_title`, v)}
                value={form?.job_title}
                placeholder="Job Title*"
                error={getFieldError(`job_title`)}
                required
                labelColour={'text-app-blue'}
                label=""
            />
            <Input
                onChange={(v) => handleInput(`company`, v)}
                value={form?.company}
                placeholder="Company*"
                error={getFieldError(`company`)}
                required
                labelColour={'text-app-blue'}
                label=""
            />
            <Select
                placeholder="Which best describes the type of organisation you work for?"
                options={organizationTypes.map((type) => ({ value: type, label: type }))}
                value={form?.organization_type ?? null}
                onChange={(v) => handleInput(`organization_type`, v)}
                error={getFieldError(`organization_type`)}
                label=""
            />
            {form?.organization_type === 'Other' && (
                <Input
                    onChange={(v) => handleInput(`organization_type_other`, v)}
                    value={form?.organization_type_other}
                    placeholder="Please specify (free text)"
                    error={getFieldError(`organization_type_other`)}
                    labelColour={'text-app-blue'}
                    label=""
                />
            )}
            <Select
                placeholder="Region"
                options={['Europe', 'North America', 'South America', 'Africa', 'Asia', 'Australia'].map((region) => ({ value: region, label: region }))}
                value={form?.region ?? null}
                onChange={(v) => handleInput(`region`, v)}
                error={getFieldError(`region`)}
                label=""
                inputId={regionInputName ? regionInputName() : undefined}
                name={regionInputName ? regionInputName() : undefined}
                autoComplete="off"
            />
            <Input
                onChange={(v) => handleInput(`dietary_requirements`, v)}
                value={form?.dietary_requirements ?? ''}
                placeholder="Please detail any dietary requirements"
                error={getFieldError(`dietary_requirements`)}
                labelColour={'text-app-blue'}
                label=""
            />
            <Input
                onChange={(v) => handleInput(`special_access_requirements`, v)}
                value={form?.special_access_requirements ?? ''}
                placeholder="Please detail any accessibility requirements"
                error={getFieldError(`special_access_requirements`)}
                labelColour={'text-app-blue'}
                label=""
            />

            <p className={"text-app-blue font-bold"}>
                The Bupa Symposium virtual platform offers the opportunity for you to connect with other attendees. Please choose "No" below if you want your contact information omitted from the platform.
            </p>

            <BooleanSelect
                placeholder="Consent to networking"
                value={ form?.consent_networking ?? null }
                onChange={ ( v ) => handleInput ( `consent_networking`, v ) }
                error={ getFieldError ( `consent_networking` ) }
            />

            {/*<p className="text-app-blue">*/}
            {/*    The Bupa Symposium platform offers the opportunity for you to connect with other attendees. Please choose "No" below if you want your contact information omitted from the platform.*/}
            {/*</p>*/}
            <p className={"text-app-blue font-bold"}>
                A quick note to let you know that by registering to attend this event in person, you give consent to appear in event photography/videography. If you do not wish to be filmed, please let us know on the event day during checkin.
            </p>
            <Input
                onChange={(v) => handleInput(`password`, v)}
                value={form?.password ?? ''}
                placeholder="Password"
                error={getFieldError(`password`)}
                type="password"
                labelColour={'text-app-blue'}
                label=""
            />
            <Input
                onChange={(v) => handleInput(`password_confirmation`, v)}
                value={form?.password_confirmation ?? ''}
                placeholder="Reconfirm Password"
                error={getFieldError(`password_confirmation`)}
                type="password"
                labelColour={'text-app-blue'}
                label=""
            />
        </>
    );
};

const ExternalVirtualForm = ( { form, getFieldError, handleInput, regionInputName } ) => {
    return (
        <>
            <Input
                onChange={(v) => handleInput(`first_name`, v)}
                value={form?.first_name}
                placeholder="First Name*"
                error={getFieldError(`first_name`)}
                required
                labelColour={'text-app-blue'}
                label=""
            />
            <Input
                onChange={(v) => handleInput(`last_name`, v)}
                value={form?.last_name}
                placeholder="Last Name*"
                error={getFieldError(`last_name`)}
                required
                labelColour={'text-app-blue'}
                label=""
            />
            <Input
                onChange={(v) => handleInput(`email`, v)}
                type="email"
                value={form?.email}
                placeholder="Email Address*"
                error={getFieldError(`email`)}
                required
                labelColour={'text-app-blue'}
                label=""
                disabled={true}
            />
            <Input
                onChange={(v) => handleInput(`job_title`, v)}
                value={form?.job_title}
                placeholder="Job Title*"
                error={getFieldError(`job_title`)}
                required
                labelColour={'text-app-blue'}
                label=""
            />
            <Input
                onChange={(v) => handleInput(`company`, v)}
                value={form?.company}
                placeholder="Company*"
                error={getFieldError(`company`)}
                required
                labelColour={'text-app-blue'}
                label=""
            />
            <Select
                placeholder="Which best describes the type of organisation you work for?"
                options={organizationTypes.map((type) => ({ value: type, label: type }))}
                value={form?.organization_type ?? null}
                onChange={(v) => handleInput(`organization_type`, v)}
                error={getFieldError(`organization_type`)}
                label=""
            />
            {form?.organization_type === 'Other' && (
                <Input
                    onChange={(v) => handleInput(`organization_type_other`, v)}
                    value={form?.organization_type_other}
                    placeholder="Please specify (free text)"
                    error={getFieldError(`organization_type_other`)}
                    labelColour={'text-app-blue'}
                    label=""
                />
            )}
            <Select
                placeholder="Region"
                options={regions.map((region) => ({ value: region, label: region }))}
                value={form?.region ?? null}
                onChange={(v) => handleInput(`region`, v)}
                error={getFieldError(`region`)}
                label=""
                inputId={regionInputName ? regionInputName() : undefined}
                name={regionInputName ? regionInputName() : undefined}
                autoComplete="off"
            />

            <p className={"text-app-blue font-bold"}>
                The Bupa Symposium virtual platform offers the opportunity for you to connect with other attendees. Please choose "No" below if you want your contact information omitted from the platform.
            </p>

            <BooleanSelect
                placeholder="Consent to networking"
                value={ form?.consent_networking ?? null }
                onChange={ ( v ) => handleInput ( `consent_networking`, v ) }
                error={ getFieldError ( `consent_networking` ) }
            />

            <Input
                onChange={(v) => handleInput(`password`, v)}
                value={form?.password ?? ''}
                placeholder="Password"
                error={getFieldError(`password`)}
                type="password"
                labelColour={'text-app-blue'}
                label=""
            />
            <Input
                onChange={(v) => handleInput(`password_confirmation`, v)}
                value={form?.password_confirmation ?? ''}
                placeholder="Reconfirm Password"
                error={getFieldError(`password_confirmation`)}
                type="password"
                labelColour={'text-app-blue'}
                label=""
            />
        </>
    );
};

export default RegistrationForm;

